// src/lib/positionRank.js
// 役職名からランクを算出する共通ロジック（組織図・緊急連絡網・社員連絡先で共用）。
// 数値が大きいほど上位。部長・課長などの固定呼称に加え、
// マネージャー・リーダー等の会社独自の呼称にも対応する。

export function positionRank(pos) {
  if (!pos) return 0;
  const p = String(pos);
  if (p.includes('会長')) return 98;
  if (p.includes('副社長')) return 95;
  if (p.includes('社長') || p.includes('代表取締役')) return 100;
  if (p.includes('専務')) return 90;
  if (p.includes('常務')) return 85;
  if (p.includes('取締役') || p.includes('役員')) return 80;
  if (p.includes('本部長')) return 70;
  if (p.includes('部長代理') || p.includes('次長')) return 58;
  if (p.includes('部長')) return 60;
  if (p.includes('課長代理')) return 45;
  if (p.includes('課長') || p.includes('マネージャー') || p.includes('マネジャー')) return 50;
  // 「サブチーフ」は「チーフ」を含むため、チーフより先に判定する（課長代理→課長 と同じ理由）
  if (p.includes('サブチーフ')) return 35;
  if (p.includes('係長') || p.includes('主任') || p.includes('チーフ')) return 40;
  if (p.includes('リーダー') || p.includes('リーダ') || p.includes('リード')) return 35;
  // 一般社員・スタッフは「上司」ではないため rank 0（空文字と同じ扱い）
  if (p.includes('一般') || p.includes('平社員') || p.includes('スタッフ') || p === '社員') return 0;
  return 10; // 上記以外の独自役職名は「役職あり」とみなす
}

/** 上司とみなす最低ランク（リーダー相当以上） */
export const SUPERVISOR_MIN_RANK = 35;

export const isPresident = pos =>
  Boolean(pos && (pos.includes('社長') || pos.includes('代表取締役')));

/** 取締役・役員以上（経営層） */
export const isExecutiveLevel = pos => positionRank(pos) >= 80;

/** 部長・本部長 相当 */
export const isDeptHeadLevel = pos => {
  const r = positionRank(pos);
  return r >= 60 && r < 80;
};

/** 課長・マネージャー 相当 */
export const isSectionHeadLevel = pos => {
  const r = positionRank(pos);
  return r >= 50 && r < 60;
};

/** 上司（リーダー相当以上）か */
export const isSupervisor = pos => positionRank(pos) >= SUPERVISOR_MIN_RANK;

/**
 * リストの中で最上位（ランク最大）の人を返す。
 * minRank 未満しかいなければ null（＝上司なし）。
 */
export function topByRank(list, minRank = SUPERVISOR_MIN_RANK) {
  let best = null;
  let bestRank = minRank - 1;
  for (const e of list || []) {
    const r = positionRank(e?.position);
    if (r > bestRank) {
      best = e;
      bestRank = r;
    }
  }
  return best;
}

/**
 * 上司を「下位 → 上位」の順に並べた配列（緊急連絡のエスカレーション順）。
 * 例: リーダー → 主任/係長 → 課長代理 → 課長/マネージャー → 部長 → 取締役
 */
export function supervisorsAscending(list, { excludeMemberId = null } = {}) {
  return (list || [])
    .filter(e => e && e.status !== 'RESIGNED')
    .filter(e => !excludeMemberId || e.memberId !== excludeMemberId)
    .filter(e => isSupervisor(e.position))
    .sort((a, b) => {
      const ra = positionRank(a.position);
      const rb = positionRank(b.position);
      if (ra !== rb) return ra - rb; // 下位から
      return (a.name || '').localeCompare(b.name || '', 'ja');
    });
}

/** バッジ等の見た目に使うスラッグ（CSSクラス互換） */
export function roleSlug(pos) {
  const r = positionRank(pos);
  if (r >= 80) return 'exec';
  if (r >= 60) return 'bucho';
  if (r >= 50) return 'kacho';
  if (r >= 45) return 'kacho-dai';
  if (r >= 40) return 'chief';
  if (r >= SUPERVISOR_MIN_RANK) return 'leader';
  return 'member';
}

/** バッジ表示テキスト。独自役職（マネージャー等）はその名称をそのまま出す。 */
export function roleBadge(pos) {
  const text = String(pos || '').trim();
  const slug = roleSlug(text);
  return { text: slug === 'member' ? '社員' : text, slug };
}
